import React from 'react';
import { useTranslation } from 'react-i18next';
import { VehicleType } from '../types';
import { format } from 'date-fns';
import { tr, enUS } from 'date-fns/locale';

interface VehicleTypesDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicleType: VehicleType | null;
  onEdit: () => void;
  canManage: boolean;
}

export const VehicleTypesDetailModal: React.FC<VehicleTypesDetailModalProps> = ({
  isOpen,
  onClose,
  vehicleType,
  onEdit,
  canManage,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'tr' ? tr : enUS;

  if (!isOpen || !vehicleType) return null;

  const formatDate = (date: string | Date | undefined) => {
    if (!date) return '-';
    return format(new Date(date), 'dd MMM yyyy HH:mm', { locale });
  };

  const DetailRow = ({ label, value, className = '' }: { label: string; value: React.ReactNode; className?: string }) => (
    <div className={`py-3 border-b border-gray-100 dark:border-gray-700 ${className}`}>
      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900 dark:text-white">{value || '-'}</dd>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
              <svg className="w-5 h-5 text-cyan-600 dark:text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {vehicleType.name}
              </h2>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                  vehicleType.isActive
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                }`}>
                  {vehicleType.isActive ? t('common.active') : t('common.inactive')}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <dl className="space-y-1">
            <DetailRow
              label={t('vehicleTypes.vehicleTypeName')}
              value={vehicleType.name}
            />
            <DetailRow
              label={t('vehicleTypes.licensePlate')}
              value={vehicleType.licensePlate}
            />
            <DetailRow
              label={t('common.createdAt')}
              value={formatDate(vehicleType.createdAt)}
            />
            <DetailRow
              label={t('common.updatedAt')}
              value={formatDate(vehicleType.updatedAt)}
              className="border-b-0"
            />
          </dl>
        </div>

        {/* Footer */}
        {canManage && (
          <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 rounded-b-lg">
            <button
              onClick={onEdit}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('common.edit')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
